import {
    CategoryScale,
    Chart,
    type ChartConfiguration,
    LineController,
    LineElement,
    LinearScale,
    PointElement,
} from "chart.js";
import { type Ref, computed, nextTick, onUnmounted, ref, watch } from "vue";

import { useLiveMetricsManager } from "@/composables/live-metrics-manager";
import { type types } from "@/lib/openapi";
import { useLiveManager } from "@/stores/live-store";
import { useReleaseManager } from "@/stores/release-store";

// Register Chart.js components
Chart.register(
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    LineController
);

export const TIME_RANGE_OPTIONS = [
    { label: "1 hour", value: 1 },
    { label: "6 hours", value: 6 },
    { label: "24 hours", value: 24 },
    { label: "7 days", value: 168 },
    { label: "30 days", value: 720 },
];

export interface UseMetricsDashboardOptions {
    entityId: Ref<string>;
    entityType: Ref<string>;
}

export const useMetricsDashboard = (options: UseMetricsDashboardOptions) => {
    const { entityId, entityType } = options;

    const { fetchMetrics, loading } = useLiveMetricsManager();
    const { selectedHours } = useLiveManager();
    const { activeRelease: selectedRelease } = useReleaseManager();

    const metrics = ref<types.LiveMetrics | null>(null);
    const error = ref<string | null>(null);
    const chartCanvas = ref<HTMLCanvasElement | null>(null);
    const chartInstance = ref<Chart | null>(null);
    const isUpdatingChart = ref(false);

    // Helper function to safely destroy chart
    const destroyChart = () => {
        if (!chartInstance.value) {
            return;
        }

        try {
            chartInstance.value.destroy();
        } catch {
            // Ignore errors during destruction
        } finally {
            chartInstance.value = null;
        }
    };

    // Helper functions to ensure reactivity
    const getMetricValue = (key: keyof types.LiveMetrics): number => {
        const value = metrics.value?.[key];
        if (typeof value === "number") {
            return value;
        }
        return 0;
    };

    const getErrorRate = (): string => {
        const rate = metrics.value?.error_rate;
        if (typeof rate === "number") {
            return (rate * 100).toFixed(2);
        }
        return "0.00";
    };

    const eventsByTypeList = computed(() => {
        if (!metrics.value) return [];
        return Object.entries(metrics.value.events_by_type || {}).map(
            ([type, count]) => ({
                type,
                count,
            })
        );
    });

    const chartData = computed(() => {
        if (
            !metrics.value?.time_series_data ||
            !Array.isArray(metrics.value.time_series_data) ||
            metrics.value.time_series_data.length === 0
        ) {
            return {
                labels: [],
                datasets: [],
            };
        }

        const labels = metrics.value.time_series_data.map(
            (d: Record<string, unknown>) => {
                if (typeof d.timestamp === "string") {
                    const date = new Date(d.timestamp);
                    // Format based on selected time range
                    if (selectedHours.value >= 168) {
                        // 7 days or more: show day/month
                        return date.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                        });
                    } else if (selectedHours.value >= 24) {
                        // 24 hours or more but less than 7 days: show day/month and time
                        return (
                            date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                            }) +
                            " " +
                            date.toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                            })
                        );
                    } else {
                        // Less than 24 hours: show time only
                        return date.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                        });
                    }
                }
                return d.timestamp || "";
            }
        );
        const data = metrics.value.time_series_data.map(
            (d: Record<string, unknown>) => Number(d.count) || 0
        );

        const primaryColor =
            getComputedStyle(document.documentElement)
                .getPropertyValue("--stekz-primary-color")
                .trim() || "#842cff";

        return {
            labels,
            datasets: [
                {
                    label: "Events",
                    data,
                    fill: false,
                    borderColor: primaryColor,
                    backgroundColor: primaryColor,
                    tension: 0.4,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                },
            ],
        };
    });

    const chartOptions: ChartConfiguration<"line">["options"] = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Disable animations to prevent errors when destroying charts
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };

    const updateChart = () => {
        if (loading.value || isUpdatingChart.value) {
            return; // Don't update chart while loading or already updating
        }

        if (!chartCanvas.value || !metrics.value?.time_series_data) {
            return;
        }

        isUpdatingChart.value = true;

        // Destroy existing chart if it exists
        destroyChart();

        // Use nextTick to ensure DOM is ready
        nextTick(() => {
            if (!chartCanvas.value || loading.value) {
                isUpdatingChart.value = false;
                return;
            }

            // Double-check the canvas is in the DOM
            if (
                !chartCanvas.value.ownerDocument ||
                !chartCanvas.value.ownerDocument.body.contains(
                    chartCanvas.value
                )
            ) {
                isUpdatingChart.value = false;
                return;
            }

            const ctx = chartCanvas.value.getContext("2d");
            if (!ctx) {
                isUpdatingChart.value = false;
                return;
            }

            // Verify the context is valid
            try {
                ctx.save();
                ctx.restore();
            } catch {
                isUpdatingChart.value = false;
                return;
            }

            // Double-check we still have valid data and chart instance is null
            if (
                !metrics.value?.time_series_data ||
                loading.value ||
                chartInstance.value
            ) {
                isUpdatingChart.value = false;
                return;
            }

            try {
                chartInstance.value = new Chart(chartCanvas.value, {
                    type: "line",
                    data: chartData.value,
                    options: chartOptions,
                });
            } catch {
                // Chart creation failed, ignore silently
            }

            isUpdatingChart.value = false;
        });
    };

    const loadMetrics = async () => {
        if (!entityId.value || !entityType.value) {
            return;
        }

        // Destroy chart before loading new metrics
        destroyChart();

        error.value = null;
        try {
            metrics.value = await fetchMetrics(
                entityId.value,
                entityType.value,
                selectedHours.value,
                selectedRelease.value || undefined
            );
        } catch (e) {
            error.value =
                e instanceof Error ? e.message : "Failed to load metrics";
        }
    };

    // Watchers
    watch(
        [metrics, chartCanvas],
        () => {
            if (loading.value) {
                return; // Don't update chart while loading
            }
            if (metrics.value?.time_series_data && chartCanvas.value) {
                // Use nextTick with a small delay to ensure DOM is fully ready
                nextTick(() => {
                    setTimeout(() => {
                        updateChart();
                    }, 100);
                });
            }
        },
        { flush: "post" }
    );

    watch(
        [entityId, entityType],
        () => {
            loadMetrics();
        },
        { immediate: true }
    );

    watch([selectedHours, selectedRelease], () => {
        loadMetrics();
    });

    onUnmounted(() => {
        if (chartInstance.value) {
            chartInstance.value.destroy();
        }
    });

    return {
        // State
        metrics,
        error,
        loading,
        chartCanvas,
        selectedHours,
        selectedRelease,
        // Computed
        eventsByTypeList,
        // Constants
        timeRangeOptions: TIME_RANGE_OPTIONS,
        // Methods
        getMetricValue,
        getErrorRate,
    };
};
